<template>
  <div
    class="scheduler-modal-wrapper"
    :class="{ 'scheduler-modal-wrapper--active': saveFeedResponse }"
  >
    <template>
      <mds-loader v-show="modalLoading" class="loader" />
    </template>
    <template>
      <div
        v-show="!modalLoading && !saveFeedResponse"
        class="scheduler-modal-wrapper"
        :class="{ active: saveFeedResponse }"
      >
        <mds-button
          v-if="editedFeed"
          variation="icon-only"
          icon="remove"
          type="button"
          class="scheduler-modal__close"
          @click="$emit('close', $event)"
        >
          Close
        </mds-button>
        <scheduler-modal-header
          :editedFeed="editedFeed"
          :active-step="activeStep"
          @mds-step-activated="activeStep = $event.step.id"
          @mds-step-clicked="activeStep = $event.id"
          @stepper-click="onStepperClick"
        />
        <scheduler-modal-content
          :source-step-info="sourceStepInfo"
          :parameters-step-info="parametersStepInfo"
          :format-step-info="formatStepInfo"
          :delivery-step-info="deliveryStepInfo"
          :source-type="sourceType"
          :data-set-type="dataSetType"
          :stepper-id="activeStep"
          :without-feed-name="emptyFeedName"
          :investment-id="investmentId"
          :holdings-entitlements="holdingsEntitlements"
          @input="addParametersToForm"
          @name="
            addParameterToForm($event, 'job_name')
            name = $event
          "
          @source-type="
            addParameterToForm($event, 'investment_object_type')
            sourceType = $event
          "
          @source-data-set-type="
            addParameterToForm($event, 'data_set_type')
            dataSetType = $event
          "
          @validName="validName = $event"
          @disable-save-button="disabledSaveButtons = $event"
          @attestation-check="attestationCheck = $event"
          v-on="$listeners"
        />
        <scheduler-modal-buttons
          :active-step="activeStep"
          :disabled-next-button="disableNextButton"
          :disabled-save-buttons="disabledSaveButtons || !isValidName"
          :disabled-edit-button="selectedParamsChanged"
          @back-step="activeStep -= 1"
          @next-step="activeStep += 1"
          @save="createOrSchedule"
          @close="$emit('close', $event)"
        />
      </div>
      <scheduler-modal-save-response
        v-show="saveFeedResponse"
        type="feed"
        :save-status="saveFeedStatus"
        :has-duplicate-name="hasDuplicateName"
        :error-loading-parameters="errorLoadingParameters"
        :attestation-check="isAttestationChecked"
        @save="createOrSchedule"
        @close="closeModal"
        @retry="reload"
        @change-name="onChangeName"
      />
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import MdsLoader from '@mds/loader'
import { MdsButton } from '@mds/button'
import SchedulerModalButtons from '@/components/schedulerModal/SchedulerModalButtons.vue'
import SchedulerModalContent from '@/components/schedulerModal/SchedulerModalContent.vue'
import SchedulerModalHeader from '@/components/schedulerModal/SchedulerModalHeader.vue'
import SchedulerModalSaveResponse from '@/components/schedulerModal/SchedulerModalSaveResponse.vue'
import {
  NOTEBOOK_PARAMETER_NAMES,
  DELIVERY_TYPES,
  SOURCE_TYPES,
  FIXED_PARAMETERS,
} from './config/constants'
import {
  compareObjects,
  FEED_COMMON_PROPS,
  INVESTMENT_PROPS,
} from '@/utils/compareObjects'
import { formatInvestmentObject } from '@/utils/feedMapper'
import { getHoldingsEntitlements } from '@/services/entitlementsDataProvider'
import { get } from 'lodash'

export default {
  name: 'SchedulerModalWrapper',
  components: {
    MdsLoader,
    MdsButton,
    SchedulerModalButtons,
    SchedulerModalContent,
    SchedulerModalHeader,
    SchedulerModalSaveResponse,
  },
  props: {
    showSchedulerModal: Boolean,
    modalLoading: Boolean,
    performanceReportParameters: {
      type: Array,
      default: /* istanbul ignore next */ () => [],
    },
    gridViewParameters: {
      type: Array,
      default: /* istanbul ignore next */ () => [],
    },
    saveFeedResponse: Boolean,
    saveFeedStatus: Boolean,
    hasDuplicateName: Boolean,
    errorLoadingParameters: Boolean,
  },
  data() {
    return {
      activeStep: 0,
      disabledSaveButtons: false,
      name: '',
      parametersForm: {},
      showModal: this.showSchedulerModal,
      sourceType: '',
      dataSetType: '',
      investmentId: '',
      validName: false,
      notebookParameters: [],
      goToLastStep: false,
      isFeedSet: false,
      attestationCheck: false,
      holdingsEntitlements: null,
    }
  },
  async mounted() {
    const { path = '' } = this.$route ?? {}
    const url_paths = path.split('/').filter((path) => !isEmpty(path))
    if (url_paths.indexOf('scheduler') > -1) {
      const { query = {} } = this.$route
      this.sourceType = query.idType
        ? SOURCE_TYPES[query.idType.toLowerCase()]
        : ''
      this.investmentId = String(query.id).toUpperCase() ?? ''
    }
    this.holdingsEntitlements = await getHoldingsEntitlements()
  },
  computed: {
    ...mapState({
      cache: (state) => state.cache.cache,
    }),
    editedFeed() {
      return this.cache.editedFeed
    },
    sourceStepIsLoading() {
      return this.cache.parametersLoader
    },
    parameters() {
      return [
        this.gridViewParameters,
        this.performanceReportParameters,
        this.sourceType,
      ]
    },
    emptyFeedName() {
      return !!(this.name || isEmpty(this.name))
    },
    disableNextButton() {
      if (this.activeStep === 0) {
        return (
          (!this.sourceType || !this.dataSetType) &&
          this.sourceType !== SOURCE_TYPES.performancereport
        )
      }

      if (this.activeStep === 1) {
        const options =
          this.sourceType === SOURCE_TYPES.performancereport
            ? this.parametersStepInfo[0]?.options?.performance_report
            : this.parametersStepInfo[0]?.options?.[this.sourceType]

        const { morningstar = [], user = [] } = options ?? {}

        return !morningstar.length && !user.length
      }

      return this.sourceStepIsLoading
    },
    selectedParamsChanged() {
      if (this.editedFeed) {
        const propsToCompare =
          this.parametersForm.investment_object_type ===
          NOTEBOOK_PARAMETER_NAMES.performance_report
            ? FEED_COMMON_PROPS
            : [...FEED_COMMON_PROPS, ...INVESTMENT_PROPS]

        return compareObjects(
          {
            ...this.parametersForm,
            name: this.parametersForm.job_name,
          },
          this.editedFeed,
          propsToCompare
        )
      }
      return false
    },
    sourceStepInfo() {
      const [investmentType] = this.notebookParameters.filter(
        (parameter) => parameter.name === NOTEBOOK_PARAMETER_NAMES.source_type
      )
      return investmentType ?? {}
    },
    parametersStepInfo() {
      return (
        this.notebookParameters.filter(
          ({ name }) =>
            name === NOTEBOOK_PARAMETER_NAMES.data_set ||
            name === NOTEBOOK_PARAMETER_NAMES.investment_item ||
            name === NOTEBOOK_PARAMETER_NAMES.performance_report
        ) ?? {}
      )
    },
    formatStepInfo() {
      return this.notebookParameters.filter(
        (parameter) =>
          !parameter.name.includes('delivery') &&
          !parameter.name.includes('investment') &&
          !parameter.name.includes('data_set') &&
          !parameter.name.includes('save_result') &&
          !parameter.name.includes('attestation_form')
      )
    },
    deliveryStepInfo() {
      const deliveryTypes = this.notebookParameters.find(
        (parameter) =>
          parameter?.name === NOTEBOOK_PARAMETER_NAMES.delivery_destination
      )
      if (
        deliveryTypes &&
        deliveryTypes.options &&
        deliveryTypes.options.delivery_destination
      ) {
        return Object.entries(deliveryTypes.options.delivery_destination).map(
          (type) => {
            const [typeName, destinations] = type
            return {
              deliveryType: DELIVERY_TYPES[typeName] ?? typeName,
              destinations,
            }
          }
        )
      }
      return []
    },
    isValidName() {
      return this.validName
    },
    isAttestationChecked() {
      return this.attestationCheck
    },
  },
  watch: {
    activeStep(value) {
      // When creating a new feed, we retrieve the parameters at the second step.
      if (value === 1 && !this.editedFeed) {
        this.$emit('get-parameters', this.sourceType)
      }
    },
    sourceType(value) {
      // If the source has changed for an edited feed, we need to call the endpoint to retrieve the related parameters.
      if (
        this.editedFeed &&
        !isEqual(
          formatInvestmentObject(this.editedFeed.investment_object_type),
          value
        )
      ) {
        this.$emit('get-parameters', value)
      }
      // Reset dataSetType if the sourceType is 'performancereport'
      if (value === SOURCE_TYPES.performancereport) {
        this.dataSetType = ''
      }
    },
    parameters: {
      immediate: true,
      deep: true,
      handler: 'setNotebookParameters',
    },
    cache: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (!isEmpty(newValue.editedFeed) && !this.isFeedSet) {
          this.setEditFeed()
        }
      },
    },
  },
  methods: {
    createOrSchedule(scheduleFeed) {
      this.$emit('save', {
        parameters: this.parametersForm,
        scheduleFeed,
      })
    },
    addParametersToForm(param) {
      if (!param) {
        return
      }

      const [key] = Object.keys(param)
      const [value] = Object.values(param)
      this.parametersForm = {
        ...this.parametersForm,
        [key]: value || '',
      }
    },
    addParameterToForm(param, key) {
      this.parametersForm[key] = param || ''
    },
    filteredParametersForm() {
      this.parametersForm = Object.entries(this.parametersForm).reduce(
        (acc, [key, value]) => {
          if (FIXED_PARAMETERS.includes(key)) {
            return {
              ...acc,
              [key]: value,
            }
          }
          return acc
        },
        {}
      )
    },
    setNotebookParameters() {
      if (this.sourceType === SOURCE_TYPES.performancereport) {
        this.notebookParameters = this.performanceReportParameters

        this.filteredParametersForm()
      } else {
        this.notebookParameters = this.gridViewParameters
      }
    },
    closeModal(modalStatus) {
      this.showModal = modalStatus
      this.$emit('close')
    },
    setEditFeed() {
      const { feedInfo, ...rest } = this.editedFeed

      this.parametersForm = {
        job_name: feedInfo?.name ?? '',
        investment_item:
          this.editedFeed?.investment_item ??
          this.editedFeed?.performance_report_id,
        ...rest,
      }

      this.sourceType =
        formatInvestmentObject(this.editedFeed.investment_object_type) ??
        this.editedFeed.investment_object_type

      if (!this.parametersForm.data_set_type) {
        this.dataSetType = 'basic'
      } else {
        this.dataSetType = this.parametersForm.data_set_type
      }

      if (!this.goToLastStep) {
        this.activeStep = 3
        this.goToLastStep = true
      }

      this.isFeedSet = true
    },
    onChangeName() {
      this.activeStep = 0
      this.$emit('hide-feedback-message')
    },
    reload() {
      this.$emit('get-parameters', this.sourceType)
    },
    onStepperClick(itemId) {
      if (itemId < this.activeStep) {
        this.activeStep = itemId
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.scheduler-modal-wrapper {
  display: flex;
  flex-direction: column;
  flex: auto;
  position: relative;

  &--active {
    justify-content: center;
    align-items: center;
  }
}

.scheduler-modal__close {
  position: absolute;
  right: 0;
}

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
